import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FinancialBankAccount } from '@gipi-financial/bank-account/models/bank-account.model';
import { FinancialBankAccountService } from '@gipi-financial/bank-account/services/bank-account.service';
import { FinancialCashierShift } from '@gipi-financial/cashier-shift/models/cashier-shift.model';
import { FinancialCashierShiftService } from '@gipi-financial/cashier-shift/services/cashier-shift.service';

import { FinancialBankAccountSelectDTO } from '@gipi-financial/bank-account/models/dto/bank-account-select.dto';
import { FinancialTypeChargeType } from '@gipi-financial/charge-type/enums/charge-type.enum';
import { FinancialChargeType } from '@gipi-financial/charge-type/models/charge-type.model';
import { FinancialChargeTypeService } from '@gipi-financial/charge-type/services/charge-type.service';
import { FinancialClient } from '@gipi-financial/client/models/client.model';
import { FinancialClientSelectDTO } from '@gipi-financial/client/models/dto/client-select.dto';
import { FinancialClientService } from '@gipi-financial/client/services/client.service';
import { FinancialConfiguration } from '@gipi-financial/configuration/models/configuration.model';
import { FinancialConfigurationService } from '@gipi-financial/configuration/services/configuration.service';
import { FinancialCreditClientOriginEnum } from '@gipi-financial/credit-client/enums/credit-client-origin.enum';
import { FinancialCreditClientType, FinancialCreditClientTypeEnum } from '@gipi-financial/credit-client/enums/credit-client-type.enum';
import { FinancialCreditClient } from '@gipi-financial/credit-client/models/credit-client.model';
import { FinancialCreditClientDTO } from '@gipi-financial/credit-client/models/dto/credit-client.dto';
import { FinancialCreditClientService } from '@gipi-financial/credit-client/services/credit-client.service';
import { FinancialPdfCreditClientReceiptService } from '@gipi-financial/credit-client/services/pdf-credit-client-receipt.service';
import { FinancialPostingCategorySelectDTO } from '@gipi-financial/posting-category/models/dto/posting-category-select.dto';
import { FinancialPostingCategoryService } from '@gipi-financial/posting-category/services/posting-category.service';
import { OAuthUser } from '@gipi-financial/user/models/user.model';
import { APP_MESSAGES, ArrayUtil, AuthenticationService, DialogService, GIPIAbstractComponent, GIPIBaseService, GIPIPageModel, GIPISortModel, INJECTOR, ObjectUtil, StringUtil, TokenDTO } from '@gipisistemas/ng-core';
import { finalize } from 'rxjs/operators';
import { CreditClientCardData, CreditClientCardDialogComponent } from '../credit-client-card-dialog/credit-client-card-dialog.component';
import { CreditClientCheckData, CreditClientCheckDialogComponent } from '../credit-client-check-dialog/credit-client-check-dialog.component';

export interface CreditClientData {
    creditType: FinancialCreditClientType;
}

@Component({
    templateUrl: './credit-client-form-dialog.component.html',
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
    `]
})
export class CreditClientFormDialogComponent extends GIPIAbstractComponent implements OnInit, OnDestroy {

    public creditTypeEnum: typeof FinancialCreditClientTypeEnum = FinancialCreditClientTypeEnum;
    public creditTypeValue: FinancialCreditClientType = 'CREDIT';

    /** Por hora o valor é fixo, poderá mudar quando tiver que chamar essa tela de outros lugares */
    public originLaunchValue: string = FinancialCreditClientOriginEnum['MANUAL'];

    public issuanceDate: Date = new Date();

    public entity: FinancialCreditClientDTO = this._newEntity();

    public client: FinancialClientSelectDTO = null;
    public postingCategory: FinancialPostingCategorySelectDTO = null;
    public chargeType: FinancialChargeType = null;

    public currentAmount: number = 0;

    public bankAccountList: FinancialBankAccount[] = [];
    public bankAccount: FinancialBankAccount = null;

    public printReceiptAfterPosting: boolean = true;

    private _cashierShift: FinancialCashierShift = null;

    clientFindByValueFn = async (value: string, page: number) => {
        const configuration: FinancialConfiguration = this.baseService.sessionStorageService.get('configuration');
        let propertySort: string = 'person.name';
        if (!ObjectUtil.isNull(configuration) && configuration.showBusinessNameInReceiptQuery) {
            propertySort = 'person.fantasyName';
        }

        const result: GIPIPageModel<FinancialClientSelectDTO> = await this._clientService.findByValue<FinancialClientSelectDTO>(value, page, 10, new GIPISortModel(propertySort, 'ASC')).toPromise();
        return result;
    };
    clientFn = (obj: FinancialClientSelectDTO) => this._clientService.getDescription(obj);

    postingCategoryFindByValueFn = async (value: string, page: number) => {
        const result: GIPIPageModel<FinancialPostingCategorySelectDTO> = await this._postingCategoryService.findByValue(value, page, 10, new GIPISortModel('fullCode', 'ASC'), 'v1', this.creditTypeValue, true).toPromise();
        return result;
    };
    postingCategoryFn = (obj: FinancialPostingCategorySelectDTO) => `${obj.fullCode} - ${obj.description}`;

    chargeTypeFindByValueFn = async (value: string, page: number) => {
        const baseTypes: FinancialTypeChargeType[] = ['ACCOUNT_DEBIT', 'DEPOSIT', 'MONEY', 'OTHER', 'PIX', 'TRANSFER'];
        const creditTypes: FinancialTypeChargeType[] = ['CHECK', 'CREDIT_CARD', 'DEBIT_CARD'];
        const types: FinancialTypeChargeType[] = this.creditTypeValue === 'CREDIT' ? [...baseTypes, ...creditTypes] : baseTypes;

        const result: GIPIPageModel<FinancialChargeType> = await this._chargeTypeService.findByValue(value, page, 50, new GIPISortModel('description', 'ASC'), '', types).toPromise();
        return result;
    };

    constructor(
        protected service: FinancialCreditClientService,
        protected baseService: GIPIBaseService,
        protected activatedRoute: ActivatedRoute,
        public dialogRef: MatDialogRef<CreditClientFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreditClientData = { creditType: 'CREDIT' },
        private _dialogService: DialogService,
        private _authenticationService: AuthenticationService<OAuthUser>,
        private _configurationService: FinancialConfigurationService,
        private _cashierShiftService: FinancialCashierShiftService,
        private _clientService: FinancialClientService,
        private _chargeTypeService: FinancialChargeTypeService,
        private _postingCategoryService: FinancialPostingCategoryService,
        private _bankAccountService: FinancialBankAccountService,
        private _pdfCreditClientReceiptService: FinancialPdfCreditClientReceiptService
    ) {
        super(baseService, activatedRoute);
        this.dialogRef.disableClose = true;
    }

    ngOnInit() {
        super.ngOnInit();
        this.creditTypeValue = this.data.creditType;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    protected async onInitForm(): Promise<void> {
        this.loading = true;

        if (this._userIsCashier()) {
            this._getCashierShiftOpenedByUser(true).then(async cashierShift => {
                if (!ObjectUtil.isNull(cashierShift)) {
                    this._cashierShift = ObjectUtil.clone(cashierShift);

                    this.bankAccountList = await this._getBankAccountByCashierShift(cashierShift);
                    if (ArrayUtil.isEmpty(this.bankAccountList)) {
                        this.close('RELOAD_TABLE');
                    } else {
                        this.bankAccount = ObjectUtil.clone(this.bankAccountList[0]);
                    }
                } else {
                    this.close('REMARK_SELECTED');
                }
            });
        } else {
            this.bankAccount = null;
            this._cashierShift = null;

            const pageBankAccount: GIPIPageModel<FinancialBankAccount> = await this._bankAccountService.findAllEnabled(0, 100).toPromise();
            if (!ObjectUtil.isNull(pageBankAccount) && !ArrayUtil.isEmpty(pageBankAccount.content)) {
                this.bankAccountList = pageBankAccount.content;
            }
        }

        this._configurationService.findEnabled()
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(configuration => {
                let postingCategoryConfig: FinancialPostingCategorySelectDTO = null;
                if (!ObjectUtil.isNull(configuration)) {
                    if (this.data.creditType === 'CREDIT') {
                        postingCategoryConfig = FinancialPostingCategorySelectDTO.transformToDTO(configuration.defaultClientCreditCategoryToCredit);
                    } else {
                        postingCategoryConfig = FinancialPostingCategorySelectDTO.transformToDTO(configuration.defaultClientCreditCategoryToDebit);
                    }
                }

                if (!ObjectUtil.isNull(postingCategoryConfig)) {
                    this.postingCategory = ObjectUtil.clone(postingCategoryConfig);
                }
            }, error => {
                this.loading = false;
                this.handleError(error);
            });
    }

    private _newEntity(): FinancialCreditClientDTO {
        const entity: FinancialCreditClientDTO = new FinancialCreditClientDTO();
        entity.amount = 0;
        entity.bankAccountId = null;
        entity.cashierShiftId = null;
        entity.chargeTypeId = null;
        entity.clientId = null;
        entity.date = new Date();
        entity.description = '';
        entity.documentNumber = '';
        entity.originLaunch = 'MANUAL';
        entity.postingCategoryId = null;
        entity.type = 'CREDIT';
        entity.enabled = true;
        return entity;
    }

    private _isValid(): boolean {
        if (ObjectUtil.isNull(this.entity)) {
            return;
        }
        if ((this.creditTypeValue === 'DEBIT') && (this.currentAmount <= 0)) {
            this.addWarningMessage('Lançamento de débito não permitido pois o cliente não possui saldo disponível');
            return;
        }
        if ((this.creditTypeValue === 'DEBIT') && (this.currentAmount < this.entity.amount)) {
            this.addWarningMessage('Lançamento de débito não permitido pois o saldo do cliente é inferior ao valor informado');
            return;
        }
        if (!this.UUIDIsValid(this.entity.clientId)) {
            this.addWarningMessage('Campo cliente é obrigatório e não foi informado');
            return false;
        }
        if (!this.UUIDIsValid(this.entity.postingCategoryId)) {
            this.addWarningMessage('Campo categoria é obrigatório e não foi informado');
            return false;
        }
        if (!this.UUIDIsValid(this.entity.chargeTypeId)) {
            this.addWarningMessage('Campo tipo de cobrança é obrigatório e não foi informado');
            return false;
        }
        if (!this.entity.amount || this.entity.amount <= 0) {
            this.addWarningMessage('Campo valor é obrigatório e não foi informado');
            return false;
        }
        if (!this.UUIDIsValid(this.entity.bankAccountId)) {
            this.addWarningMessage('Campo conta bancária é obrigatório e não foi informado');
            return false;
        }
        if (StringUtil.isEmpty(this.entity.description)) {
            this.addWarningMessage('Campo descrição é obrigatório e não foi informado');
            return false;
        }
        if (this.entity.description.length < 3) {
            this.addWarningMessage('Campo descrição deve conter no mínimo 3 caracteres');
            return false;
        }

        return true;
    }

    public confirm(): void {
        try {
            this.entity.clientId = null;
            if (!ObjectUtil.isNull(this.client)) {
                this.entity.clientId = this.client.id;
            }

            this.entity.postingCategoryId = null;
            if (!ObjectUtil.isNull(this.postingCategory)) {
                this.entity.postingCategoryId = this.postingCategory.id;
            }

            this.entity.chargeTypeId = null;
            if (!ObjectUtil.isNull(this.chargeType)) {
                this.entity.chargeTypeId = this.chargeType.id;
            }

            this.entity.bankAccountId = null;
            if (!ObjectUtil.isNull(this.bankAccount)) {
                this.entity.bankAccountId = this.bankAccount.id;
            }

            this.entity.cashierShiftId = null;
            if (!ObjectUtil.isNull(this._cashierShift)) {
                this.entity.cashierShiftId = this._cashierShift.id;
            }

            if (!this._isValid()) {
                return;
            }
            this.loading = true;

            this.entity.type = this.creditTypeValue;

            const chargeType = this.chargeType.type;
            if (chargeType === 'CHECK') {
                this._saveByCheck();
            } else if (chargeType === 'CREDIT_CARD' || chargeType === 'DEBIT_CARD') {
                this._saveByCard();
            } else {
                this._saveDefault();
            }
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    private _saveDefault(): void {
        this.loading = true;

        this.service.generateCredit([this.entity]).toPromise().then((creditClienListtSaved) => {
            if (!ArrayUtil.isEmpty(creditClienListtSaved)) {
                this.addSuccessMessage(INJECTOR.get(APP_MESSAGES).SUCCESS);
                this._printReceipt(creditClienListtSaved[0]);
                this.close('RELOAD_TABLE');
            }
        }, error => {
            this.loading = false;
            throw new Error(error);
        });
    }

    private async _saveByCheck(): Promise<void> {
        this.loading = true;

        const client: FinancialClient = await this._clientService.getOne(this.client.id).toPromise().catch(e => {
            this.addErrorMessage(e);
            return null;
        });

        if (ObjectUtil.isNull(client)) {
            this.addWarningMessage('Não foi possivel realizar o lançamento pois não identificamos o cliente');
            return;
        }

        const creditClientCheckData: CreditClientCheckData = {
            creditClient: ObjectUtil.clone(this.entity),
            bankAccount: FinancialBankAccountSelectDTO.transformToDTO(this.bankAccount),
            client: FinancialClientSelectDTO.transformToDTO(client),
            postingCategory: this.postingCategory,
        };

        this._dialogService
            .open({
                componentOrTemplateRef: CreditClientCheckDialogComponent,
                data: creditClientCheckData,
                width: '75%',
            }).afterClosed().toPromise().then((creditClientSaved: FinancialCreditClient) => {
                if (!ObjectUtil.isNull(creditClientSaved)) {
                    this._printReceipt(creditClientSaved);
                    this.close('RELOAD_TABLE');
                } else {
                    this.loading = false;
                }
            });
    }

    private _saveByCard(): void {
        this.loading = true;

        const creditClientCardData: CreditClientCardData = {
            creditClient: ObjectUtil.clone(this.entity),
            bankAccount: this.bankAccount,
            chargeType: this.chargeType,
        };

        this._dialogService
            .open({
                componentOrTemplateRef: CreditClientCardDialogComponent,
                data: creditClientCardData,
                width: '75%',
            }).afterClosed().toPromise().then((creditClientSaved: FinancialCreditClient) => {
                if (!ObjectUtil.isNull(creditClientSaved)) {
                    this._printReceipt(creditClientSaved);
                    this.close('RELOAD_TABLE');
                } else {
                    this.loading = false;
                }
            });
    }

    /**
     * @template RELOAD_TABLE Dá reload na grid atualizando os registros
     * @template REMARK_SELECTED Volta a tela anterior e seleciona os registros na grid
     * @template NONE Não acontece nada, só volta para tela anterior
     */
    public close(operation: 'RELOAD_TABLE' | 'REMARK_SELECTED' | 'NONE'): void {
        this.dialogRef.close(operation);
    }

    private async _getCashierShiftOpenedByUser(closeModal: boolean): Promise<FinancialCashierShift> {
        return new Promise(async (resolve, reject) => {
            if (this._userIsCashier()) {
                await this._cashierShiftService.getOpenedByCurrentUser().toPromise().then(cashierShift => {
                    if (ObjectUtil.isNewModel(cashierShift)) {
                        resolve(null);
                        if (closeModal) {
                            this.close('REMARK_SELECTED');
                        }
                        const creditType = this.data.creditType === 'CREDIT' ? 'crédito' : 'débito';
                        this.handleError(`Não é possível realizar o lançamento de ${creditType} do cliente. Operador de caixa deve abrir um turno.`);
                    } else {
                        resolve(cashierShift);
                    }
                }, (error) => {
                    resolve(null);
                    if (error.toLowerCase().includes('a consulta não retornou resultado único')) {
                        this.handleError(`Erro ao buscar turnos. ${error}`);
                    } else {
                        this.handleError(error);
                    }
                });
            }
        });
    }

    private _userIsCashier(): boolean {
        const token: TokenDTO<OAuthUser> = this._authenticationService.token;
        return (!ObjectUtil.isNull(token) && token.user.cashier);
    }

    private async _getBankAccountByCashierShift(cashierShift: FinancialCashierShift): Promise<FinancialBankAccount[]> {
        try {
            const creditType = this.data.creditType === 'CREDIT' ? 'crédito' : 'débito';

            if (ObjectUtil.isNull(cashierShift)) {
                this.addWarningMessage(`Não é possivel realizar o lançamento de ${creditType}. Operador de caixa deve abrir um turno`);
                this.loading = false;
                return Promise.resolve([]);
            }

            const existBankAccountOther: boolean = await this._cashierShiftService.existCashierBankAccountByChargeType(cashierShift, 'OTHER');
            if (!existBankAccountOther) {
                this.addWarningMessage(`Não é possível realizar o lançamento ${creditType} pois não existe conta bancária configurada no caixa. Verifique no sistema legado`);
                this.loading = false;
                return Promise.resolve([]);
            }

            const bankAccountList: FinancialBankAccount[] = cashierShift.cashier.cashierBankAccountList.filter(e => e.chargeType.type === 'OTHER').map(e => e.bankAccount);
            if (ArrayUtil.isEmpty(bankAccountList)) {
                this.addWarningMessage(`Não é possível realizar o lançamento de ${creditType}, pois não existe conta bancária configurada no caixa`);
                this.loading = false;
                return Promise.resolve([]);
            }

            return Promise.resolve(bankAccountList);
        } catch (e) {
            this.loading = false;
            this.addErrorMessage(e);
            return Promise.resolve([]);
        }
    }

    public validateBankAccountByCashierShift(chargeType?: FinancialChargeType): void {
        try {
            if (this._userIsCashier()) {
                this.bankAccountList = [];
                let bankAccountListAux: FinancialBankAccount[] = [];

                if ((chargeType.type === 'PIX') || (chargeType.type === 'DEPOSIT') || (chargeType.type === 'TRANSFER')) {
                    bankAccountListAux = this._cashierShift.cashier.cashierBankAccountList
                        .filter(cba => cba.chargeType.type === chargeType.type)
                        .map(cba => cba.bankAccount);
                }

                // Se não tiver nenhuma configuração de conta bancária para o tipo de cobrança selecionado ou for diferente de PIX, DEPOSIT, e TRANSFER ele pega a outros.
                if (ArrayUtil.isEmpty(bankAccountListAux)) {
                    bankAccountListAux = this._cashierShift.cashier.cashierBankAccountList
                        .filter(cba => cba.chargeType.type === 'OTHER')
                        .map(cba => cba.bankAccount);

                    // Se não tiver nenhuma configuração de conta bancária para o tipo de cobrança outros ele mostra a mensagem para que seja configurado.
                    if (ArrayUtil.isEmpty(bankAccountListAux)) {
                        const creditType = this.data.creditType === 'CREDIT' ? 'crédito' : 'débito';
                        this.addWarningMessage(`Não é possível realizar o lançamento do ${creditType} com o tipo de cobrança selecionado, pois não existe conta bancária configurada no caixa`);
                        return;
                    } else {
                        this.bankAccountList = bankAccountListAux;
                    }
                } else {
                    this.bankAccountList = bankAccountListAux;
                }

                if (!ArrayUtil.isEmpty(this.bankAccountList) && this.bankAccountList.length === 1) {
                    this.bankAccount = ObjectUtil.clone(this.bankAccountList[0]);
                }
            }
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    public selectionChangeClient(client: FinancialClientSelectDTO): void {
        if (ObjectUtil.isNull(client)) {
            return;
        }

        this.service.balance(client.id).subscribe(currentBalance => {
            this.currentAmount = currentBalance || 0;
        }, error => {
            this.loading = false;
            this.handleError(error);
        });
    }

    private async _printReceipt(entity: FinancialCreditClient): Promise<void> {
        try {
            if (!this.printReceiptAfterPosting) {
                return;
            }

            if (ObjectUtil.isNull(entity) || ObjectUtil.isNull(entity.client)) {
                this.addWarningMessage('Não foi possível obter os dados para impressão do recibo');
                return;
            }

            const client = await this._clientService.getOne(entity.client.id).toPromise();
            if (ObjectUtil.isNull(client)) {
                this.addWarningMessage('Não foi possível obter os dados do cliente para impressão do recibo');
                return;
            }

            this._pdfCreditClientReceiptService.exportPdf({
                showTwoWays: true,
                creditClientAmount: entity.amount,
                creditClientClient: ObjectUtil.clone(client),
                creditClientDate: entity.date,
                creditClientDescription: entity.description,
                creditClientType: entity.type,
                creditCanceled: false,
            });
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

}
